import wishlistService from '../../services/wishlistService';
import { fetchCart } from './cartActions';

// Action Types
export const ADD_TO_WISHLIST = "ADD_TO_WISHLIST";
export const DELETE_FROM_WISHLIST = "DELETE_FROM_WISHLIST";
export const DELETE_ALL_FROM_WISHLIST = "DELETE_ALL_FROM_WISHLIST";
export const SET_WISHLIST_FROM_BACKEND = "SET_WISHLIST_FROM_BACKEND";
export const WISHLIST_LOADING = "WISHLIST_LOADING";
export const WISHLIST_ERROR = "WISHLIST_ERROR";
export const UPSERT_WISHLIST_ITEM = "UPSERT_WISHLIST_ITEM";
export const REMOVE_WISHLIST_ITEM = "REMOVE_WISHLIST_ITEM";

/**
 * Fetch wishlist from backend and sync to Redux
 */
export const fetchWishlist = () => {
  return async dispatch => {
    dispatch({ type: WISHLIST_LOADING, payload: true });
    try {
      const result = await wishlistService.getWishlist();
      if (result.success) {
        dispatch({
          type: SET_WISHLIST_FROM_BACKEND,
          payload: {
            items: result.items || [],
            count: result.count || 0
          }
        });
      }
      dispatch({ type: WISHLIST_LOADING, payload: false });
      return result;
    } catch (error) {
      dispatch({ type: WISHLIST_ERROR, payload: error.message });
      dispatch({ type: WISHLIST_LOADING, payload: false });
      return { success: false, error: error.message };
    }
  };
};

/**
 * Toggle wishlist item via backend API (add if not present, remove if already in)
 * @param {Object} product - Product object with id
 * @param {Function} addToast - Toast notification function
 */
export const toggleWishlistAsync = (product, addToast) => {
  return async dispatch => {
    try {
      const productId = product.id || product.product_id;
      if (!productId) {
        return {
          success: false,
          error: 'Missing product id'
        };
      }

      const result = await wishlistService.toggleWishlist(productId);

      if (result.success) {
        if (result.data?.items) {
          dispatch({
            type: SET_WISHLIST_FROM_BACKEND,
            payload: {
              items: result.data.items || [],
              count: result.data.count || 0
            }
          });
        }
        if (result.action === 'added' && result.item) {
          dispatch({
            type: UPSERT_WISHLIST_ITEM,
            payload: result.item
          });
        }
        if (result.action === 'removed') {
          dispatch({
            type: REMOVE_WISHLIST_ITEM,
            payload: { productId }
          });
        }
        // Refresh wishlist from backend
        dispatch(fetchWishlist());

        if (addToast) {
          if (result.action === 'added') {
            addToast("Added to Wishlist", { appearance: "success", autoDismiss: true });
          } else {
            addToast("Removed from Wishlist", { appearance: "warning", autoDismiss: true });
          }
        }
      } else {
        if (addToast) {
          addToast(result.error || "Failed to update wishlist", { appearance: "error", autoDismiss: true });
        }
      }

      return result;
    } catch (error) {
      if (addToast) {
        addToast("Failed to update wishlist", { appearance: "error", autoDismiss: true });
      }
      return { success: false, error: error.message };
    }
  };
};

/**
 * Add item to wishlist via backend API
 * @param {Object} product - Product object with id
 * @param {Function} addToast - Toast notification function
 */
export const addToWishlistAsync = (product, addToast) => {
  return async dispatch => {
    try {
      const productId = product.id || product.product_id;
      if (!productId) {
        return { success: false, error: 'Missing product id' };
      }
      const result = await wishlistService.toggleWishlist(productId);

      if (result.success) {
        dispatch(fetchWishlist());

        if (addToast) {
          if (result.action === 'added') {
            addToast("Added to Wishlist", { appearance: "success", autoDismiss: true });
          } else {
            // Item was already in wishlist, was removed
            addToast("Already in Wishlist (Removed)", { appearance: "info", autoDismiss: true });
          }
        }
      } else {
        if (addToast) {
          addToast(result.error || "Failed to add to wishlist", { appearance: "error", autoDismiss: true });
        }
      }

      return result;
    } catch (error) {
      if (addToast) {
        addToast("Failed to add to wishlist", { appearance: "error", autoDismiss: true });
      }
      return { success: false, error: error.message };
    }
  };
};

/**
 * Remove item from wishlist via backend API
 * @param {Object} product - Product object with id
 * @param {Function} addToast - Toast notification function
 */
export const removeFromWishlistAsync = (product, addToast) => {
  return async dispatch => {
    try {
      // Toggle will remove if already present
      const productId = product.product?.id || product.id || product.product_id;
      if (!productId) {
        return { success: false, error: 'Missing product id' };
      }
      const result = await wishlistService.toggleWishlist(productId);

      if (result.success) {
        dispatch(fetchWishlist());

        if (addToast) {
          addToast("Removed from Wishlist", { appearance: "warning", autoDismiss: true });
        }
      } else {
        if (addToast) {
          addToast(result.error || "Failed to remove from wishlist", { appearance: "error", autoDismiss: true });
        }
      }

      return result;
    } catch (error) {
      if (addToast) {
        addToast("Failed to remove from wishlist", { appearance: "error", autoDismiss: true });
      }
      return { success: false, error: error.message };
    }
  };
};

/**
 * Move wishlist item to cart via backend API
 * @param {Object} wishlistItem - Wishlist item with id
 * @param {number} quantity - Quantity to add to cart
 * @param {Function} addToast - Toast notification function
 */
export const moveToCartAsync = (wishlistItem, quantity = 1, addToast) => {
  return async dispatch => {
    try {
      const wishlistItemId = wishlistItem.wishlistItemId || wishlistItem.id;
      const result = await wishlistService.moveToCart(wishlistItemId, quantity);

      if (result.success) {
        // Refresh both wishlist and cart
        dispatch(fetchWishlist());
        dispatch(fetchCart());

        if (addToast) {
          addToast("Moved to Cart", { appearance: "success", autoDismiss: true });
        }
      } else {
        if (addToast) {
          addToast(result.error || "Failed to move to cart", { appearance: "error", autoDismiss: true });
        }
      }

      return result;
    } catch (error) {
      if (addToast) {
        addToast("Failed to move to cart", { appearance: "error", autoDismiss: true });
      }
      return { success: false, error: error.message };
    }
  };
};

// ==========================================
// Legacy actions (kept for backward compatibility)
// These work with local Redux state only
// ==========================================

/**
 * Add to wishlist (local Redux only)
 * @deprecated Use addToWishlistAsync for backend integration
 */
export const addToWishlist = (item, addToast) => {
  return dispatch => {
    if (addToast) {
      addToast("Added To Wishlist", {
        appearance: "success",
        autoDismiss: true
      });
    }
    dispatch({ type: ADD_TO_WISHLIST, payload: item });
  };
};

/**
 * Delete from wishlist (local Redux only)
 * @deprecated Use removeFromWishlistAsync for backend integration
 */
export const deleteFromWishlist = (item, addToast) => {
  return dispatch => {
    if (addToast) {
      addToast("Removed From Wishlist", {
        appearance: "error",
        autoDismiss: true
      });
    }
    dispatch({ type: DELETE_FROM_WISHLIST, payload: item });
  };
};

/**
 * Delete all from wishlist (local Redux only)
 * @deprecated Use backend integration
 */
export const deleteAllFromWishlist = addToast => {
  return dispatch => {
    if (addToast) {
      addToast("Removed All From Wishlist", {
        appearance: "error",
        autoDismiss: true
      });
    }
    dispatch({ type: DELETE_ALL_FROM_WISHLIST });
  };
};
